import * as React from "react"
import { ViewIcon, ViewOffSlashIcon } from '@hugeicons-pro/core-solid-rounded'
import { Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { HugeiconsIcon } from '@hugeicons/react'

import { cn } from "@/lib/utils"

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: "default" | "atrium" | "white" | "error"
  icon?: React.ReactNode
  showClearButton?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant = "white", icon, showClearButton = false, value, onChange, ...props }, ref) => {
    const hasValue = value !== undefined && value !== null && value !== ''
    const showClear = showClearButton && hasValue
    const isPassword = type === 'password'
    const [showPassword, setShowPassword] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement | null>(null)

    // Combine refs: both the forwarded ref and our internal ref
    const setRefs = React.useCallback((node: HTMLInputElement | null) => {
      inputRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    }, [ref])

    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (onChange) {
        const syntheticEvent = {
          target: { value: '' },
          currentTarget: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
      // Focus the input after clearing
      inputRef.current?.focus()
    }

    const handleTogglePassword = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setShowPassword(!showPassword)
      // Focus the input after toggling
      inputRef.current?.focus()
    }

    // Determine the actual input type
    const inputType = isPassword && showPassword ? 'text' : type

    // Calculate right padding based on what buttons are shown
    const getRightPadding = () => {
      if (showClear && isPassword) return "pr-20" // Both buttons: clear + password
      if (showClear) return "pr-8" // Only clear button
      if (isPassword) return "pr-12" // Only password toggle
      // Default padding for variants when no buttons
      if (variant === "atrium") return "pr-12"
      return "pr-4" // default, white, error
    }

    const inputElement = (
      <input
        type={inputType}
        data-slot="input"
        className={cn(
          "border-accent-900/20 file:text-ink flex h-9 w-full min-w-0 border bg-transparent px-3 py-3 text-base shadow-xs transition-all duration-150 outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:cursor-not-allowed disabled:opacity-50 text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
          variant === "atrium" && "h-12 bg-atrium-content text-white placeholder:text-white/50 focus-visible:border-white/50 px-4 outline-none ring-0 focus-visible:ring-accent-600 rounded-[13px] border-atrium-border",
          variant === "white" &&
            "h-11 rounded-lg focus-visible:ring-offset-0 px-4 outline-none ring-0 focus-visible:ring-accent-600 border-atrium-border focus-visible:border-accent-600 text-atrium-black placeholder:text-atrium-black/40 bg-white hover:border-atrium-black/20 hover:bg-atrium-content focus-visible:bg-white focus-visible:ring-offset-white dark:bg-atrium-inner dark:hover:border-atrium-border-dark dark:hover:bg-atrium-content dark:focus-visible:bg-atrium-inner dark:focus-visible:ring-offset-atrium-background",
          variant === "error" &&
            "h-11 rounded-lg focus-visible:ring-offset-0 px-4 outline-none ring-0 border-red-500 text-red-700 placeholder:text-atrium-black/40 bg-white hover:border-red-500 hover:bg-atrium-content focus-visible:bg-atrium-content focus-visible:ring-red-500 focus-visible:border-red-500 focus-visible:ring-offset-white dark:bg-atrium-inner dark:text-red-400 dark:hover:bg-atrium-content/90 dark:hover:border-red-400 dark:focus-visible:bg-atrium-inner dark:focus-visible:ring-offset-atrium-background",
          icon && "pl-8",
          getRightPadding(),
          className
        )}
        ref={setRefs}
        value={value}
        onChange={onChange}
        {...props}
      />
    )

    if (icon || showClear || isPassword) {
      return (
        <div className="relative w-full">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10 flex items-center">
              {icon}
            </div>
          )}
          {isPassword && (
            <button
              type="button"
              onClick={handleTogglePassword}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center text-atrium-black/60 hover:text-atrium-black transition-colors disabled:pointer-events-none disabled:opacity-50"
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={props.disabled}
            >
              <HugeiconsIcon 
                icon={showPassword ? ViewOffSlashIcon : ViewIcon} 
                className="size-4" 
              />
            </button>
          )}
          {showClear && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                "absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-5 h-5 rounded-full hover:bg-atrium-black/10 text-atrium-black/60 hover:text-atrium-black transition-colors",
                isPassword ? "right-14" : "right-3"
              )}
              aria-label="Clear input"
            >
              <HugeiconsIcon icon={Cancel01Icon} className="size-[14px]" />
            </button>
          )}
          {inputElement}
        </div>
      )
    }

    return inputElement
  }
)
Input.displayName = "Input"

export { Input }
